import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { CountriesDataService, CountriesColumnsService } from '../../services';
import { DialogCountryFormComponent } from './country-form/country-form.component';
import { DialogCountryDetailsComponent } from './country-details/country-details.component';
import { Country, CountriesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-countries',
  templateUrl: './countries.component.html',
  styleUrls: ['./countries.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class CountriesComponent implements OnInit, OnDestroy {

  private countryAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private countryEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private countryDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public countryToDelete: Country = {};
  public countries: Country[] = [];
  public country: Country = {};
  public selectedCountries: Country[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private countriesDataService: CountriesDataService,
    private countriesColumnsService: CountriesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('CountriesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Countries - Management');
    this.primengConfig.ripple = true;
    this.getCountries();
  }

  ngOnDestroy(): void {
    console.log('CountriesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getCountries(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.countriesDataService.getCountries(this.parameters).subscribe((response: CountriesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.countries = response.results;
        } else {
          this.countries = this.countries.concat(response.results);
        }
        if (response.inlineCount > this.countries.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getCountries();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COUNTRIESNOTLOADED') });
    });
  }

  importCountries(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.countriesDataService.addCountries(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COUNTRIESCREATED') });
                if (proceed === j) {
                  this.getCountries();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COUNTRIESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COUNTRIESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.countriesDataService.updateCountries(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COUNTRIESUPDATED') });
                if (proceed === j) {
                  this.getCountries();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COUNTRIESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COUNTRIESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.countriesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('countries.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedCountries() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Countries?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.countriesDataService.deleteCountries(this.selectedCountries).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Countries Deleted', life: 3000 });
            this.getCountries();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Countries not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Countries not deleted' });
        });
        this.selectedCountries = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.countries, this.columns, 'Countries');
  }

  exportExcel() {
    ToolsService.exportExcel(this.countries, this.columns, 'Countries');
  }

  exportCsv() {
    ToolsService.exportCsv(this.countries, this.columns, 'Countries');
  }

  refresh() {
    this.countriesDataService.clearCache();
    this.getCountries();
  }

  countryAdd(): void {
    if (!this.navigation.getParamValue('countries.addInDialog', true)) {
      this.router.navigate(['/countries/add']);
      return;
    }
    this.countryAddDialog = this.dialogService.open(DialogCountryFormComponent, {
      header: '', // this.translate.instant('NEWCOUNTRY'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        country: { id: null },
        users: this.countriesColumnsService.users,
      },
    });

    this.countryAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Country added' });
        this.getCountries();
      }
    });
  }

  countryEdit(country: Country): void {
    if (!this.navigation.getParamValue('countries.editInDialog', true)) {
      this.router.navigate(['/countries/edit/' + country.id]);
      return;
    }
    this.countryEditDialog = this.dialogService.open(DialogCountryFormComponent, {
      header: '', // this.translate.instant('EDITCOUNTRY') + ' ' + country.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        country: country,
        users: this.countriesColumnsService.users,
      },
    });

    this.countryEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Country updated' });
        this.getCountries();
      }
    });
  }

  countryDetails(country: Country): void {
    if (!this.navigation.getParamValue('countries.detailsInDialog', true)) {
      this.router.navigate(['/countries/details/' + country.id]);
      return;
    }
    this.countryDetailsDialog = this.dialogService.open(DialogCountryDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        country: country,
      }
    });
  }

  countryDelete(country: Country): void {
    this.countryToDelete = country;
    this.messageService.clear();
    this.messageService.add({
      key: 'countryDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + country.name + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.countriesDataService.deleteCountry(this.countryToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Country deleted' });
      this.getCountries();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Country not deleted' });
    });
  }

  onDeleteReject(): void {
    this.countryToDelete = {};
    this.messageService.clear('countryDelete');
  }

}
